import React from "react";

/**
 * Captures and persists marketing attribution data.
 * - First-touch: stored ONCE on first visit, never overwritten.
 * - Latest-touch: updated on every visit / new UTM-bearing landing.
 * Returns an attribution object that should be attached to every form submission.
 *
 * SECURITY POSTURE — localStorage is intentional and appropriate here.
 * Stored keys (fbd_attr_first / fbd_attr_latest) contain ONLY non-sensitive
 * marketing attribution data:
 *   - UTM parameters (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 *   - document.referrer (public hop-in URL)
 *   - window.location.href of the landing page (public URL)
 *   - derived "source" label (utm_source / referrer host / "direct")
 *   - ISO timestamps of first and latest touches
 * No PII is stored: no names, emails, phone numbers, addresses, IDs, tokens,
 * audit answers, or business data ever land in localStorage. Even under a
 * successful XSS an attacker gains only the marketing provenance strings that
 * are already visible on the URL itself, so migrating to HttpOnly cookies
 * would add complexity without a corresponding security gain. If this
 * boundary ever changes (e.g. we start caching form drafts), migrate the
 * new keys to secure cookies or in-memory state.
 */
const FIRST_TOUCH_KEY = "fbd_attr_first";
const LATEST_TOUCH_KEY = "fbd_attr_latest";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function readQueryParams() {
  if (typeof window === "undefined") return {};
  const sp = new URLSearchParams(window.location.search);
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = sp.get(k);
    if (v) out[k] = v;
  });
  return out;
}

function deriveSource(utms, referrer) {
  if (utms.utm_source) return utms.utm_source;
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.replace(/^www\./, "");
      if (host && !host.includes(window.location.hostname.replace(/^www\./, ""))) {
        return host;
      }
    } catch (err) {
      // Referrer was not a parseable URL — fall through to "direct".
      void err;
    }
  }
  return "direct";
}

function readJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (err) {
    // Storage may be disabled or value corrupted — fail closed.
    void err;
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Storage may be disabled (private mode, quota) — non-blocking.
    void err;
  }
}

function currentTouch() {
  const utms = readQueryParams();
  const referrer = document.referrer || "";
  return {
    utms,
    referrer,
    landing_page: window.location.href,
    at: new Date().toISOString(),
    source: deriveSource(utms, referrer),
  };
}

function ensureFirstTouch(touch) {
  const stored = readJSON(FIRST_TOUCH_KEY);
  if (stored) return stored;
  const first = { ...touch.utms, referrer: touch.referrer, landing_page: touch.landing_page, source: touch.source, at: touch.at };
  writeJSON(FIRST_TOUCH_KEY, first);
  return first;
}

function resolveLatestTouch(touch) {
  const prev = readJSON(LATEST_TOUCH_KEY);
  const hasNewUtms = UTM_KEYS.some((k) => touch.utms[k]);
  const referrerChanged = touch.referrer && (!prev || prev.referrer !== touch.referrer);
  if (!(hasNewUtms || referrerChanged || !prev)) return prev;
  const latest = { ...touch.utms, referrer: touch.referrer, landing_page: touch.landing_page, source: touch.source, at: touch.at };
  writeJSON(LATEST_TOUCH_KEY, latest);
  return latest;
}

function mergeAttribution(first, latest, fallbackLandingPage) {
  return {
    utm_source: latest.utm_source || first.utm_source || null,
    utm_medium: latest.utm_medium || first.utm_medium || null,
    utm_campaign: latest.utm_campaign || first.utm_campaign || null,
    utm_content: latest.utm_content || first.utm_content || null,
    utm_term: latest.utm_term || first.utm_term || null,
    referrer: latest.referrer || null,
    landing_page: first.landing_page || fallbackLandingPage,
    first_touch_source: first.source || null,
    latest_touch_source: latest.source || null,
    first_touch_at: first.at || null,
    latest_touch_at: latest.at || null,
  };
}

export function useAttribution() {
  const [attribution, setAttribution] = React.useState({});

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const touch = currentTouch();
    const first = ensureFirstTouch(touch);
    const latest = resolveLatestTouch(touch);
    setAttribution(mergeAttribution(first, latest, touch.landing_page));
  }, []);

  return attribution;
}

export function nowISO() {
  return new Date().toISOString();
}
